import type {
  CSSProperties,
  DragEventHandler,
  PointerEventHandler,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Check, MapPin, User, X } from 'lucide-react'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import { JobStatusBadge } from '@/components/shared/StatusBadges'
import { JOB_STATUS_BADGE_STYLE } from '@/components/shared/jobStatusColors'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/format'
import { formatSlotForCard, slotLabel } from './grid-helpers'
import { jobPreloadTargetProps } from './job-detail-preload'
import {
  JOB_STATUS,
  JOB_STATUS_LABELS,
  INTERVENTION_TYPE,
  PAYMENT_OPTION,
  type Job,
} from '@/types'

export interface JobEntryCardProps {
  job: Job
  // Technician accent colour (border / left rail) + translucent fill.
  color: string
  tint: string
  // Visually flags an overlapping (conflicting) slot (AC-2.3.1-009/-039).
  conflict?: boolean
  onClick: () => void
  // Compact mode for short agenda slots and dense week cells.
  compact?: boolean
  variant?: 'day' | 'week'
  className?: string
  draggable?: boolean
  onDragStart?: DragEventHandler<HTMLButtonElement>
  onDragEnd?: DragEventHandler<HTMLButtonElement>
  onResizeStart?: PointerEventHandler<HTMLSpanElement>
  ariaDisabled?: boolean
  isMutating?: boolean
  resizable?: boolean
  resizeEndLabel?: string | null
  realtimeActive?: boolean
  style?: CSSProperties
}

function colorWithAlpha(value: string, alpha: number): string {
  const trimmed = value.trim()
  const shortHex = /^#([0-9a-f]{3})$/i.exec(trimmed)
  const longHex = /^#?([0-9a-f]{6})$/i.exec(trimmed)
  const hex = shortHex
    ? shortHex[1]
        .split('')
        .map(char => char + char)
        .join('')
    : longHex?.[1]

  if (!hex) return trimmed

  const numeric = Number.parseInt(hex, 16)
  const red = (numeric >> 16) & 255
  const green = (numeric >> 8) & 255
  const blue = numeric & 255

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

function conflictShellStyle(color: string): CSSProperties {
  return {
    borderColor: color,
    boxShadow: `0 0 0 1px ${color}, 0 8px 16px -8px ${colorWithAlpha(color, 0.45)}`,
  }
}

function conflictTintStyle(tint: string): CSSProperties {
  return {
    backgroundColor: tint,
  }
}

function conflictChipStyle(color: string): CSSProperties {
  return {
    backgroundColor: colorWithAlpha(color, 0.12),
    borderColor: colorWithAlpha(color, 0.24),
    color,
  }
}

function LocationText({
  value,
  className,
}: {
  value: string
  className: string
}) {
  return (
    <TruncatedTextTooltip
      content={value}
      className={className}
      contentClassName='max-w-[240px] break-words text-xs'
    >
      {value}
    </TruncatedTextTooltip>
  )
}

function CityText({ value, className }: { value: string; className: string }) {
  return (
    <span
      className={cn(
        'block min-w-0 max-w-full whitespace-normal break-words',
        className
      )}
    >
      {value}
    </span>
  )
}

const HOVER_FLOAT_CLASS =
  'transform-gpu transition-[box-shadow,transform] duration-150 ease-out hover:z-30 hover:-translate-y-0.5 hover:shadow-[0_10px_18px_-10px_rgba(38,45,66,0.32),0_4px_8px_-6px_rgba(38,45,66,0.18)] active:translate-y-0'

function getPlanningTypeKey(job: Job): string | null {
  if (job.contract_id) return 'agenda.card.underContract'
  if (job.quote_id || job.payment_option === PAYMENT_OPTION.quote_on_site) {
    return 'agenda.card.siteVisitQuote'
  }
  return null
}

function getInterventionTypeKey(job: Job): string | null {
  switch (job.intervention_type) {
    case INTERVENTION_TYPE.visit_quote:
      return 'form.interventionTypes.visitQuote.label'
    case INTERVENTION_TYPE.one_time_treatment:
      return 'form.interventionTypes.oneTimeTreatment.label'
    case INTERVENTION_TYPE.under_contract:
      return 'form.interventionTypes.underContract.label'
    case INTERVENTION_TYPE.warranty_return:
      return 'form.interventionTypes.warrantyReturn.label'
    default:
      return getPlanningTypeKey(job)
  }
}

function getTerminalOverlay(job: Job) {
  if (job.status === JOB_STATUS.completed) {
    return {
      Icon: Check,
      background: 'rgba(236, 253, 245, 0.4)',
      color: '#10b981',
      strokeWidth: 2.4,
      backdropFilter: undefined,
    }
  }

  if (job.status === JOB_STATUS.cancelled) {
    return {
      Icon: X,
      background: '#FDE9E866',
      color: '#d92d20',
      strokeWidth: 2.4,
      backdropFilter: undefined,
    }
  }

  return null
}

function getJobStatusLabel(status: Job['status']): string {
  return JOB_STATUS_LABELS[status].toLocaleUpperCase()
}

// A single agenda job entry. Colour-coded by the assigned technician (NOT
// status, AC-2.3.1-005/-037); status is conveyed by a separate badge. Clickable
// -> View job details (AC-2.3.1-048).
export function JobEntryCard({
  job,
  color,
  tint,
  conflict,
  onClick,
  compact,
  variant = 'day',
  className,
  draggable,
  onDragStart,
  onDragEnd,
  onResizeStart,
  ariaDisabled,
  isMutating,
  resizable,
  resizeEndLabel,
  realtimeActive,
  style,
}: JobEntryCardProps) {
  const { t } = useTranslation('jobs')
  const slot = slotLabel(job)
  const statusStyle = JOB_STATUS_BADGE_STYLE[job.status]
  const locationPrimary = job.postal_code ?? job.city ?? '--'
  const locationSecondary = job.city ?? job.address
  const title =
    job.service_type ??
    job.reference ??
    job.client?.name ??
    job.client_name ??
    t('common.clientFallback')
  const client = job.client?.name ?? job.client_name
  const planningTypeKey = getPlanningTypeKey(job)
  const planningType = planningTypeKey ? t(planningTypeKey) : null
  const interventionTypeKey = getInterventionTypeKey(job)
  const interventionType = interventionTypeKey ? t(interventionTypeKey) : null
  const isWarrantyReturn =
    job.intervention_type === INTERVENTION_TYPE.warranty_return
  const weekDisplayType = interventionType ?? planningType ?? title
  const weekTimeLabel = formatSlotForCard(slot)
  const amountToCollect =
    (job.intervention_type === INTERVENTION_TYPE.one_time_treatment ||
      job.intervention_type === INTERVENTION_TYPE.warranty_return ||
      job.intervention_type === INTERVENTION_TYPE.under_contract) &&
    job.amount_to_collect != null &&
    job.amount_to_collect > 0
      ? formatCurrency(job.amount_to_collect, 'EUR', 2)
      : null
  const terminalOverlay = getTerminalOverlay(job)
  const conflictShell = conflict ? conflictShellStyle(color) : null
  const conflictTint = conflict ? conflictTintStyle(tint) : null
  const conflictChip = conflict ? conflictChipStyle(color) : null
  const realtimeBorderStyle: CSSProperties | null = realtimeActive
    ? { borderColor: '#ff5a1f', borderWidth: 2 }
    : null
  // Marks the card for the agenda job-detail preload (visibility + proximity).
  const preloadTargetProps = jobPreloadTargetProps(job)

  if (variant === 'week') {
    return (
      <button
        type='button'
        data-agenda-job-card='true'
        {...preloadTargetProps}
        onClick={onClick}
        draggable={draggable || undefined}
        onDragStart={draggable ? onDragStart : undefined}
        onDragEnd={onDragEnd}
        aria-disabled={ariaDisabled}
        aria-busy={isMutating || undefined}
        disabled={isMutating}
        style={{
          ...style,
          borderColor: '#e7e9ef',
          ...conflictShell,
          ...realtimeBorderStyle,
        }}
        className={cn(
          'group relative isolate flex h-20 w-full overflow-hidden rounded-[10px] border bg-white p-0 text-left shadow-[0_1px_4px_0_rgba(38,45,66,0.05),0_1px_4px_0_rgba(38,45,66,0.1)]',
          HOVER_FLOAT_CLASS,
          draggable && 'cursor-grab active:cursor-grabbing',
          isMutating && 'cursor-wait opacity-60',
          className
        )}
      >
        <span
          className='flex w-20 shrink-0 flex-col gap-1 p-2'
          style={
            conflictTint
              ? { ...conflictTint, color }
              : { backgroundColor: tint, color }
          }
        >
          <LocationText
            value={locationPrimary}
            className='truncate font-heading text-xs font-semibold leading-[1.2] text-[#171a20]'
          />
          {locationSecondary ? (
            <CityText
              value={locationSecondary}
              className='truncate font-sans text-[10px] font-medium leading-3 text-[#171a20]'
            />
          ) : null}
          <span
            className='mt-auto inline-flex min-w-[70px] items-center justify-center overflow-hidden whitespace-nowrap rounded-md border border-[#e7e9ef] bg-white px-1 py-0.5 font-sans text-[10px] font-medium leading-3 tabular-nums shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'
            style={conflictChip ? conflictChip : { color }}
          >
            <span className='whitespace-nowrap'>{weekTimeLabel}</span>
          </span>
        </span>

        <span className='flex min-w-0 flex-1 flex-col justify-between gap-2 overflow-hidden p-2'>
          <span className='flex min-w-0 flex-col gap-1'>
            <span className='flex min-w-0 items-center gap-1'>
              <ServiceCategoryIcon name={title} className='size-3.5 shrink-0' />
              <span className='min-w-0 truncate font-sans text-xs font-medium leading-4 text-[#171a20]'>
                {title}
              </span>
            </span>
            <span
              className={cn(
                'inline-flex h-[14px] w-fit max-w-full items-center justify-center rounded-md px-1.5 py-0.5 font-sans text-[8px] font-medium uppercase leading-[10px] shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]',
                isWarrantyReturn &&
                  'border border-[#9de100] bg-[#f1f7e3] text-[#518704]'
              )}
              style={isWarrantyReturn ? undefined : statusStyle}
            >
              <span className='truncate'>{weekDisplayType}</span>
            </span>
          </span>
          <span className='min-w-0 border-t border-[#e7e9ef] pt-1 font-sans text-[10px] font-medium leading-3 text-[#3d434e]'>
            <span className='min-w-0 truncate'>{client ?? '--'}</span>
          </span>
        </span>

        {terminalOverlay ? (
          <span
            aria-hidden='true'
            className='pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[10px]'
            style={{
              backgroundColor: terminalOverlay.background,
              backdropFilter: terminalOverlay.backdropFilter,
            }}
          >
            <terminalOverlay.Icon
              className='h-10 w-10'
              strokeWidth={terminalOverlay.strokeWidth}
              style={{ color: terminalOverlay.color }}
            />
          </span>
        ) : null}
        <span
          aria-hidden='true'
          className='pointer-events-none absolute bottom-0 right-0 top-0 z-20 w-1 rounded-r-[10px]'
          style={{ backgroundColor: color }}
        />
      </button>
    )
  }

  if (compact) {
    return (
      <button
        type='button'
        data-agenda-job-card='true'
        {...preloadTargetProps}
        onClick={onClick}
        draggable={draggable || undefined}
        onDragStart={draggable ? onDragStart : undefined}
        onDragEnd={onDragEnd}
        aria-disabled={ariaDisabled}
        aria-busy={isMutating || undefined}
        disabled={isMutating}
        style={{
          ...style,
          borderColor: '#e7e9ef',
          ...conflictShell,
          ...realtimeBorderStyle,
        }}
        className={cn(
          'group relative isolate flex min-h-[64px] w-full overflow-hidden rounded-xl border bg-white p-0 text-left shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_1px_4px_0_rgba(38,45,66,0.1)]',
          HOVER_FLOAT_CLASS,
          draggable && 'cursor-grab active:cursor-grabbing',
          isMutating && 'cursor-wait opacity-60',
          className
        )}
      >
        <span
          className='flex w-[90px] shrink-0 flex-col gap-1 p-2'
          style={conflictTint ?? { backgroundColor: tint }}
        >
          <MapPin
            className='h-3 w-3 shrink-0'
            style={{ color }}
            aria-hidden='true'
          />
          <LocationText
            value={locationPrimary}
            className='min-w-0 truncate font-heading text-xl font-bold leading-[1.2] text-[#171a20]'
          />
          {locationSecondary ? (
            <CityText
              value={locationSecondary}
              className='font-sans text-xs font-medium leading-4 text-[#171a20]'
            />
          ) : null}
          <span
            className='mt-auto inline-flex min-w-[70px] max-w-full items-center justify-center rounded-md border border-[#e7e9ef] bg-white px-1 py-0.5 font-sans text-[10px] font-medium leading-3 tabular-nums'
            style={conflictChip ? conflictChip : { color }}
          >
            <span className='whitespace-nowrap'>{formatSlotForCard(slot)}</span>
          </span>
        </span>
        <span className='flex min-w-0 flex-1 flex-col gap-2 p-2 pr-3'>
          <span className='flex min-w-0 items-center gap-1'>
            <ServiceCategoryIcon name={title} className='h-4 w-4 shrink-0' />
            <span className='min-w-0 truncate font-sans text-xs font-medium leading-4 text-[#171a20]'>
              {title}
            </span>
          </span>
          <span className='flex min-h-7 flex-col items-stretch gap-1'>
            {interventionType ? (
              <span
                className={cn(
                  'inline-flex w-fit max-w-full flex-col rounded-md border px-1.5 py-0.5 font-sans text-[8px] font-medium uppercase leading-[10px] shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]',
                  isWarrantyReturn
                    ? 'border-[#9de100] bg-[#f1f7e3] text-[#518704]'
                    : 'border-[#e7e9ef] bg-[#f4f5f8] text-[#171a20]'
                )}
              >
                <span className='truncate'>{interventionType}</span>
                {amountToCollect ? (
                  <span
                    className={cn(
                      'truncate text-[10px] leading-4',
                      isWarrantyReturn ? 'text-[#518704]' : 'text-[#CE7E0A]'
                    )}
                  >
                    · {amountToCollect}
                  </span>
                ) : null}
              </span>
            ) : null}
            <JobStatusBadge
              status={job.status}
              label={getJobStatusLabel(job.status)}
              className='ml-auto w-fit shrink-0 rounded-md px-1.5 py-0.5 font-sans text-[8px] uppercase leading-[10px] shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'
            />
          </span>
          <span className='mt-auto flex min-w-0 items-center gap-1 border-t border-[#e7e9ef] pt-1 font-sans text-[10px] font-medium leading-3 text-[#3d434e]'>
            <User className='h-3 w-3 shrink-0 text-[#555b6a]' />
            <span className='min-w-0 truncate'>{client ?? '--'}</span>
          </span>
        </span>
        {terminalOverlay ? (
          <span
            aria-hidden='true'
            className='pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl'
            style={{
              backgroundColor: terminalOverlay.background,
              backdropFilter: terminalOverlay.backdropFilter,
            }}
          >
            <terminalOverlay.Icon
              className='h-12 w-12'
              strokeWidth={terminalOverlay.strokeWidth}
              style={{ color: terminalOverlay.color }}
            />
          </span>
        ) : null}
        <span
          aria-hidden='true'
          className='pointer-events-none absolute bottom-0 right-0 top-0 z-20 w-1 rounded-r-xl'
          style={{ backgroundColor: color }}
        />
        {resizeEndLabel ? (
          <span className='pointer-events-none absolute right-2 top-2 z-10 rounded-md bg-[#171a20] px-2 py-1 text-[11px] font-bold tabular-nums text-white shadow-lg'>
            {resizeEndLabel}
          </span>
        ) : null}
        {resizable ? (
          <span
            role='separator'
            aria-orientation='horizontal'
            aria-label={t('agenda.resizeDuration')}
            onPointerDown={onResizeStart}
            style={{ '--resize-color': color } as CSSProperties}
            className='absolute bottom-0 left-1/2 z-20 h-3 w-12 -translate-x-1/2 cursor-ns-resize rounded-full bg-transparent after:absolute after:left-1/2 after:top-1/2 after:h-1 after:w-8 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-[var(--resize-color)] after:opacity-0 after:transition-opacity hover:after:opacity-70 group-hover:after:opacity-45'
          />
        ) : null}
      </button>
    )
  }

  return (
    <button
      type='button'
      data-agenda-job-card='true'
      {...preloadTargetProps}
      onClick={onClick}
      draggable={draggable || undefined}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={onDragEnd}
      aria-disabled={ariaDisabled}
      aria-busy={isMutating || undefined}
      disabled={isMutating}
      style={{
        ...style,
        borderColor: '#e7e9ef',
        ...conflictShell,
        ...realtimeBorderStyle,
      }}
      className={cn(
        'group relative isolate flex w-full overflow-hidden rounded-xl border bg-white p-0 text-left shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_1px_4px_0_rgba(38,45,66,0.1)]',
        HOVER_FLOAT_CLASS,
        draggable && 'cursor-grab active:cursor-grabbing',
        isMutating && 'cursor-wait opacity-60',
        className
      )}
    >
      <span
        className='flex w-[90px] shrink-0 flex-col gap-1 p-2'
        style={
          conflictTint
            ? { ...conflictTint, color }
            : { background: tint, color }
        }
      >
        <span className='flex min-w-0 flex-col gap-1'>
          <MapPin className='h-3 w-3 shrink-0' />
          <LocationText
            value={locationPrimary}
            className='truncate font-heading text-xl font-bold leading-[1.2] text-[#171a20]'
          />
          {locationSecondary ? (
            <CityText
              value={locationSecondary}
              className='font-sans text-xs font-medium leading-4 text-[#171a20]'
            />
          ) : null}
        </span>
        <span
          className='mt-auto inline-flex min-w-[70px] max-w-full justify-center rounded-md border border-[#e7e9ef] bg-white px-1 py-0.5 font-sans text-[10px] font-medium leading-3 tabular-nums'
          style={conflictChip ?? undefined}
        >
          <span className='whitespace-nowrap'>{formatSlotForCard(slot)}</span>
        </span>
      </span>

      <span className='flex min-h-0 min-w-0 flex-1 flex-col gap-1 overflow-hidden p-2 pr-3'>
        <span className='flex min-h-0 min-w-0 items-start gap-1 overflow-hidden'>
          <ServiceCategoryIcon name={title} className='h-4 w-4 shrink-0' />
          <span className='line-clamp-2 min-w-0 font-sans text-xs font-medium leading-4 text-[#171a20]'>
            {title}
          </span>
        </span>
        <span className='flex min-w-0 shrink-0 flex-col items-stretch gap-1'>
          {interventionType || planningType ? (
            <span
              className={cn(
                'inline-flex w-fit max-w-full flex-col rounded-md border px-1.5 py-0.5 font-sans text-[8px] font-medium uppercase leading-[10px] shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]',
                isWarrantyReturn
                  ? 'border-[#9de100] bg-[#f1f7e3] text-[#518704]'
                  : 'border-[#e7e9ef] bg-[#f4f5f8] text-[#171a20]'
              )}
            >
              <span className='truncate'>
                {interventionType ?? planningType}
              </span>
              {amountToCollect ? (
                <span
                  className={cn(
                    'truncate text-[10px] font-semibold leading-4',
                    isWarrantyReturn ? 'text-[#518704]' : 'text-[#CE7E0A]'
                  )}
                >
                  · {amountToCollect}
                </span>
              ) : null}
            </span>
          ) : null}
          <JobStatusBadge
            status={job.status}
            label={getJobStatusLabel(job.status)}
            className='ml-auto w-fit shrink-0 rounded-md px-1.5 py-0.5 font-sans text-[8px] uppercase leading-[10px] shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'
          />
        </span>
        <span className='mt-auto flex min-w-0 shrink-0 items-center gap-1 border-t border-[#e7e9ef] pt-1 font-sans text-[10px] font-medium leading-3 text-[#3d434e]'>
          <User className='h-3 w-3 shrink-0 text-[#555b6a]' />
          <span className='min-w-0 truncate'>{client ?? '--'}</span>
        </span>
      </span>
      {terminalOverlay ? (
        <span
          aria-hidden='true'
          className='pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl'
          style={{
            backgroundColor: terminalOverlay.background,
            backdropFilter: terminalOverlay.backdropFilter,
          }}
        >
          <terminalOverlay.Icon
            className='h-12 w-12'
            strokeWidth={terminalOverlay.strokeWidth}
            style={{ color: terminalOverlay.color }}
          />
        </span>
      ) : null}
      <span
        aria-hidden='true'
        className='pointer-events-none absolute bottom-0 right-0 top-0 z-20 w-1 rounded-r-xl'
        style={{ backgroundColor: color }}
      />
      {resizeEndLabel ? (
        <span className='pointer-events-none absolute right-2 top-9 z-10 rounded-md bg-[#171a20] px-2 py-1 text-[11px] font-bold tabular-nums text-white shadow-lg'>
          {resizeEndLabel}
        </span>
      ) : null}
      {resizable ? (
        <span
          role='separator'
          aria-orientation='horizontal'
          aria-label={t('agenda.resizeDuration')}
          onPointerDown={onResizeStart}
          style={{ '--resize-color': color } as CSSProperties}
          className='absolute bottom-1 left-1/2 z-20 h-3 w-12 -translate-x-1/2 cursor-ns-resize rounded-full bg-transparent opacity-0 transition-opacity after:absolute after:left-1/2 after:top-1/2 after:h-1 after:w-8 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-[var(--resize-color)] after:opacity-70 group-hover:opacity-100'
        />
      ) : null}
    </button>
  )
}
